import {
  CreatePromptGroupBody,
  CreatePromptTemplateBody,
  ListPromptTemplatesParams,
  PromptArchiveResult,
  PromptContextResponse,
  PromptContextSubject,
  PromptGroupRecord,
  PromptGroupWriteResult,
  PromptHiddenResult,
  PromptKeepMineResult,
  PromptRecentTemplateRecord,
  PromptsConfig,
  PromptTemplateRecord,
  PromptUsageRecord,
  PromptVersionsResponse,
  UpdatePromptGroupBody,
  UpdatePromptTemplateBody,
} from '@/types/prompts';

import api from './axiosInstance';

/** JWT Surface group `prompts` (Xano group 290). */
const PROMPTS_API_URL = 'https://api.vh3connect.io/api:S38FjQ2W/prompts';

const url = (path: string) => `${PROMPTS_API_URL}/${path}`;

/** Only the params that narrow the list; empty ones are left off the request. */
const listParams = ({
  agent_id,
  group_id,
  scope,
  archived,
  search,
}: ListPromptTemplatesParams) => ({
  ...(agent_id && { agent_id }),
  ...(group_id && { group_id }),
  ...(scope && { scope }),
  ...(archived && { archived: true }),
  ...(search?.trim() && { search: search.trim() }),
});

/** The lookup's query string for one subject; none for no subject. */
const contextParams = (subject: PromptContextSubject | null) => {
  if (!subject) return {};
  if (subject.kind === 'job') return { jobId: subject.id };
  if (subject.kind === 'document') return { documentId: subject.id };
  return { quoteDocumentId: subject.id };
};

export const promptsApi = {
  /**
   * Context Variables (spec FSI #1230): VH3's list and the values found for
   * at most one subject, from Xano API 2935.
   */
  getContext: async (
    subject: PromptContextSubject | null
  ): Promise<PromptContextResponse> => {
    const { data } = await api.get<PromptContextResponse>(url('context'), {
      params: contextParams(subject),
    });
    return data;
  },

  getConfig: async (): Promise<PromptsConfig> => {
    const { data } = await api.get<PromptsConfig>(url('config'));
    return data;
  },

  listTemplates: async (
    params: ListPromptTemplatesParams = {}
  ): Promise<PromptTemplateRecord[]> => {
    const { data } = await api.get<{ items: PromptTemplateRecord[] }>(
      url('templates'),
      { params: listParams(params) }
    );
    return data?.items ?? [];
  },

  listGroups: async (): Promise<PromptGroupRecord[]> => {
    const { data } = await api.get<{ items: PromptGroupRecord[] }>(
      url('groups')
    );
    return data?.items ?? [];
  },

  /** A new personal template, version 1. */
  createTemplate: async (
    body: CreatePromptTemplateBody
  ): Promise<PromptTemplateRecord> => {
    const { data } = await api.post<PromptTemplateRecord>(
      url('templates'),
      body
    );
    return data;
  },

  /** Title, body or variables save a new Template Version. */
  updateTemplate: async (
    templateId: number,
    body: UpdatePromptTemplateBody
  ): Promise<PromptTemplateRecord> => {
    const { data } = await api.patch<PromptTemplateRecord>(
      url(`templates/${templateId}`),
      body
    );
    return data;
  },

  archiveTemplate: async (templateId: number): Promise<PromptArchiveResult> => {
    const { data } = await api.post<PromptArchiveResult>(
      url(`templates/${templateId}/archive`)
    );
    return data;
  },

  unarchiveTemplate: async (
    templateId: number
  ): Promise<PromptArchiveResult> => {
    const { data } = await api.post<PromptArchiveResult>(
      url(`templates/${templateId}/unarchive`)
    );
    return data;
  },

  /**
   * The tenant's team copy of a VH3 default (`edit_prompts`); it replaces the
   * default in the menu and the list.
   */
  copyTemplate: async (templateId: number): Promise<PromptTemplateRecord> => {
    const { data } = await api.post<PromptTemplateRecord>(
      url(`templates/${templateId}/copy`)
    );
    return data;
  },

  /** Hide or unhide a VH3 default for the tenant (`edit_prompts`). */
  setTemplateHidden: async (
    templateId: number,
    hidden: boolean
  ): Promise<PromptHiddenResult> => {
    const { data } = await api.post<PromptHiddenResult>(
      url(`templates/${templateId}/${hidden ? 'hide' : 'unhide'}`)
    );
    return data;
  },

  createGroup: async (
    body: CreatePromptGroupBody
  ): Promise<PromptGroupRecord> => {
    const { data } = await api.post<PromptGroupRecord>(url('groups'), body);
    return data;
  },

  /** On a VH3 group Xano stores a tenant override; the VH3 row never changes. */
  updateGroup: async (
    groupId: number,
    body: UpdatePromptGroupBody
  ): Promise<PromptGroupWriteResult> => {
    const { data } = await api.patch<PromptGroupWriteResult>(
      url(`groups/${groupId}`),
      body
    );
    return data;
  },

  /** Hide or unhide a VH3 group for the tenant (`edit_prompts`). */
  setGroupHidden: async (
    groupId: number,
    hidden: boolean
  ): Promise<PromptHiddenResult> => {
    const { data } = await api.post<PromptHiddenResult>(
      url(`groups/${groupId}/${hidden ? 'hide' : 'unhide'}`)
    );
    return data;
  },

  /** Tenant groups only. */
  archiveGroup: async (groupId: number): Promise<PromptArchiveResult> => {
    const { data } = await api.post<PromptArchiveResult>(
      url(`groups/${groupId}/archive`)
    );
    return data;
  },

  /** One template the caller can see (VH3 defaults included). */
  getTemplate: async (templateId: number): Promise<PromptTemplateRecord> => {
    const { data } = await api.get<PromptTemplateRecord>(
      url(`templates/${templateId}`)
    );
    return data;
  },

  /** Every Template Version, newest first (#596). */
  listVersions: async (templateId: number): Promise<PromptVersionsResponse> => {
    const { data } = await api.get<PromptVersionsResponse>(
      url(`templates/${templateId}/versions`)
    );
    return data;
  },

  /** Saves an old version's text as a new version ("restored from vN"). */
  restoreVersion: async (
    templateId: number,
    version: number
  ): Promise<PromptTemplateRecord> => {
    const { data } = await api.post<PromptTemplateRecord>(
      url(`templates/${templateId}/versions/${version}/restore`)
    );
    return data;
  },

  /** A team copy takes VH3's current text as a new version (`edit_prompts`). */
  takeUpdate: async (templateId: number): Promise<PromptTemplateRecord> => {
    const { data } = await api.post<PromptTemplateRecord>(
      url(`templates/${templateId}/take-update`)
    );
    return data;
  },

  /** A team copy keeps its text; the notice clears (`edit_prompts`). */
  keepMine: async (templateId: number): Promise<PromptKeepMineResult> => {
    const { data } = await api.post<PromptKeepMineResult>(
      url(`templates/${templateId}/keep-mine`)
    );
    return data;
  },

  /** Prompt Use counts for the tenant, and the caller's share. */
  listUsage: async (): Promise<PromptUsageRecord[]> => {
    const { data } = await api.get<{ items: PromptUsageRecord[] }>(
      url('usage')
    );
    return data?.items ?? [];
  },

  /** The caller's last used templates for one agent, newest first. */
  listRecent: async (
    agentId: number,
    limit = 5
  ): Promise<PromptRecentTemplateRecord[]> => {
    const { data } = await api.get<{ items: PromptRecentTemplateRecord[] }>(
      url('recent'),
      { params: { agent_id: agentId, limit } }
    );
    return data?.items ?? [];
  },
};
